/**
 * Production-Safe Logger
 * Removes debug logs in production to prevent data leakage
 */

const isDevelopment = import.meta.env.MODE === 'development' || import.meta.env.DEV === true;
const isDebugEnabled = import.meta.env.VITE_ENABLE_DEBUG === 'true';

/**
 * Safe console logging that respects environment
 */
export const logger = {
  /**
   * Debug logs - ONLY in development
   * Use for detailed debugging, data inspection
   */
  debug: (...args: unknown[]) => {
    if (isDevelopment || isDebugEnabled) {
      console.log(...args);
    }
  },

  /**
   * Info logs - Development + flagged production
   * Use for important flow tracking
   */
  info: (...args: unknown[]) => {
    if (isDevelopment || isDebugEnabled) {
      console.log(...args);
    }
  },

  /**
   * Warning logs - Always shown
   * Use for recoverable issues
   */
  warn: (...args: unknown[]) => {
    console.warn(...args);
  },

  /**
   * Error logs - Always shown
   * Use for errors that need attention
   */
  error: (...args: unknown[]) => {
    console.error(...args);
  },

  /**
   * Sanitize data before logging (remove sensitive fields)
   */
  sanitize: (data: Record<string, unknown>) => {
    const sensitive = ['password', 'token', 'secret', 'key', 'authorization', 'access_token', 'refresh_token'];
    const sanitized = { ...data };
    
    Object.keys(sanitized).forEach(key => {
      if (sensitive.some(s => key.toLowerCase().includes(s))) {
        sanitized[key] = '[REDACTED]';
      }
    });
    
    return sanitized;
  }
};

export default logger;
